import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { ScanLine, X, Camera, Upload, RotateCcw, AlertTriangle } from "lucide-react";

/**
 * Cross-platform QR scanner modal:
 *  - Mobile: rear camera via getUserMedia
 *  - Desktop: any webcam OR upload an image with the QR code
 */
export default function QRScanner({ open, onClose, onResult }) {
  const containerId = "qr-reader-container";
  const fileInputRef = useRef(null);
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const [mode, setMode] = useState("camera"); // "camera" | "file"
  const [running, setRunning] = useState(false);

  // Stop helper
  const stopCamera = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch {
      /* ignore */
    }
    setRunning(false);
  };

  // Cleanup on close / unmount
  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }
    setError(null);
    setMode("camera");

    // Discover cameras
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!devices || devices.length === 0) {
          setError("No se detectó cámara. Sube una imagen con el QR.");
          setMode("file");
          return;
        }
        setCameras(devices);
        // Prefer rear camera when label hints
        const rear =
          devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
        setActiveCamera(rear.id);
      })
      .catch((err) => {
        setError(
          "Permiso de cámara denegado o no disponible. Puedes subir una imagen con el QR."
        );
        setMode("file");
        // eslint-disable-next-line no-console
        console.warn("getCameras error", err);
      });

    return () => {
      stopCamera();
    };
  }, [open]);

  // Start camera scan when activeCamera changes
  useEffect(() => {
    if (!open || mode !== "camera" || !activeCamera) return;
    let cancelled = false;

    const start = async () => {
      await stopCamera();
      if (cancelled) return;
      try {
        const scanner = new Html5Qrcode(containerId, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        scannerRef.current = scanner;
        await scanner.start(
          activeCamera,
          {
            fps: 10,
            qrbox: { width: 240, height: 240 },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            handleResult(decodedText);
          },
          () => {
            /* scan in progress, no result yet */
          }
        );
        setRunning(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("scanner start error", err);
        setError("No se pudo iniciar la cámara. Prueba con otra o sube una imagen.");
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [open, mode, activeCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResult = async (text) => {
    await stopCamera();
    onResult?.(text);
    onClose?.();
  };

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === activeCamera);
    const next = cameras[(idx + 1) % cameras.length];
    setActiveCamera(next.id);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      // Need a fresh scanner with no active camera
      await stopCamera();
      const scanner = new Html5Qrcode(containerId, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = scanner;
      const decoded = await scanner.scanFile(file, false);
      handleResult(decoded);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("scanFile error", err);
      setError("No se detectó ningún QR en la imagen. Inténtalo de nuevo.");
    } finally {
      e.target.value = "";
    }
  };

  const switchToFile = async () => {
    await stopCamera();
    setMode("file");
    setError(null);
  };

  const switchToCamera = () => {
    setMode("camera");
    setError(null);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="qr-scanner-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-[430px] bg-white dark:bg-zinc-950 sm:rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 h-14 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
          <div className="flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-orange-500" />
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-zinc-900 dark:text-white">
              Escanear QR
            </h3>
          </div>
          <button
            onClick={async () => {
              await stopCamera();
              onClose?.();
            }}
            data-testid="qr-scanner-close"
            className="w-9 h-9 flex items-center justify-center rounded-sm hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-zinc-200 dark:border-zinc-800 shrink-0">
          <button
            onClick={switchToCamera}
            data-testid="qr-tab-camera"
            className={`flex-1 h-11 flex items-center justify-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest transition-colors ${
              mode === "camera"
                ? "text-orange-500 border-b-2 border-orange-500"
                : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            Cámara
          </button>
          <button
            onClick={switchToFile}
            data-testid="qr-tab-file"
            className={`flex-1 h-11 flex items-center justify-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest transition-colors ${
              mode === "file"
                ? "text-orange-500 border-b-2 border-orange-500"
                : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            Imagen
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-zinc-50 dark:bg-zinc-900">
          {/* Always render the container so html5-qrcode can mount even on file mode */}
          <div className="relative">
            <div
              id={containerId}
              className={`w-full bg-black rounded-md overflow-hidden ${
                mode === "camera" ? "aspect-square" : "hidden"
              }`}
              data-testid="qr-reader"
            />
            {mode === "camera" && running && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-[60%] aspect-square border-2 border-orange-500/80 rounded-md relative">
                  <div className="absolute -top-1 -left-1 w-5 h-5 border-t-4 border-l-4 border-orange-500" />
                  <div className="absolute -top-1 -right-1 w-5 h-5 border-t-4 border-r-4 border-orange-500" />
                  <div className="absolute -bottom-1 -left-1 w-5 h-5 border-b-4 border-l-4 border-orange-500" />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 border-b-4 border-r-4 border-orange-500" />
                </div>
              </div>
            )}
          </div>

          {mode === "file" && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="qr-file-pick-btn"
                className="w-full h-32 rounded-md border-2 border-dashed border-zinc-300 dark:border-zinc-700 hover:border-orange-500 transition-colors flex flex-col items-center justify-center gap-2 bg-white dark:bg-zinc-950"
              >
                <Upload className="w-7 h-7 text-orange-500" />
                <span className="font-mono-tech text-xs uppercase tracking-widest text-zinc-700 dark:text-zinc-300">
                  Seleccionar imagen con QR
                </span>
                <span className="font-mono-tech text-[10px] text-zinc-500">
                  PNG · JPG · WEBP
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
                data-testid="qr-file-input"
              />
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 rounded-sm bg-red-500/10 border border-red-500/40 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="font-mono-tech text-[11px] text-red-700 dark:text-red-400">
                {error}
              </p>
            </div>
          )}

          {mode === "camera" && cameras.length > 1 && (
            <button
              type="button"
              onClick={switchCamera}
              data-testid="qr-switch-camera"
              className="w-full mt-4 h-11 rounded-sm bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 hover:border-orange-500 transition-colors flex items-center justify-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-zinc-700 dark:text-zinc-300"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Cambiar cámara ({cameras.length})
            </button>
          )}

          <p className="mt-4 font-mono-tech text-[10px] text-zinc-500 text-center uppercase tracking-widest">
            Apunta al código QR del número de serie
          </p>
        </div>
      </div>
    </div>
  );
}
